import React from "react";
import { Card, CardContent, CardHeader, Typography, LinearProgress, Grid } from "@material-ui/core";
import { makeStyles } from "@material-ui/styles";
import { Warning, CheckCircle, Error, Info } from "@material-ui/icons";

const useStyles = makeStyles(theme => ({
  card: {
    height: "100%"
  },
  cardHeader: {
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText
  },
  riskItem: {
    padding: theme.spacing(2),
    backgroundColor: theme.palette.background.default,
    borderRadius: theme.shape.borderRadius,
    marginBottom: theme.spacing(2)
  },
  riskScore: {
    display: "flex",
    alignItems: "center",
    marginBottom: theme.spacing(1)
  },
  riskIcon: {
    marginRight: theme.spacing(1)
  },
  progress: {
    height: 8,
    borderRadius: 4
  },
  progressLabel: {
    display: "flex",
    justifyContent: "space-between",
    marginTop: theme.spacing(0.5)
  },
  recommendationBox: {
    padding: theme.spacing(2),
    backgroundColor: theme.palette.background.default,
    borderRadius: theme.shape.borderRadius,
    marginTop: theme.spacing(2)
  },
  recommendationIcon: {
    marginRight: theme.spacing(1),
    verticalAlign: "middle"
  }
}));

const getRiskLevel = (score) => {
  if (score <= 30) return { icon: <CheckCircle style={{ color: "#4CAF50" }} />, color: "#4CAF50", label: "Low" };
  if (score <= 60) return { icon: <Warning style={{ color: "#FFA726" }} />, color: "#FFA726", label: "Medium" };
  return { icon: <Error style={{ color: "#F44336" }} />, color: "#F44336", label: "High" };
};

export default function RiskAssessmentPanel({ data }) {
  const classes = useStyles();

  // Debug logging to see what data we're receiving
  console.log('RiskAssessmentPanel received data:', data);
  console.log('Risk assessment data:', data?.riskAssessment);

  // Check if we have risk assessment data from the API
  const hasRiskData = Boolean(data?.riskAssessment);

  // Map API risk data to components
  const getRiskData = () => {
    if (!hasRiskData) return [];
    
    const riskData = data.riskAssessment;
    const riskFactors = [];
    
    console.log('Processing risk assessment data:', riskData);
    
    // Overall risk (always include if we have any risk data)
    if (riskData.overall_risk_score !== undefined) {
      riskFactors.push({
        title: "Overall Risk",
        score: riskData.overall_risk_score,
        description: riskData.summary || "Combined risk assessment based on all factors"
      });
    }
    
    // Process all risk categories from the API structure
    // Climate risk
    if (riskData.climate_risk && riskData.climate_risk.risk_score !== undefined) {
      riskFactors.push({
        title: "Climate Risk",
        score: riskData.climate_risk.risk_score,
        description: "Weather patterns and climate conditions"
      });
    }
    
    // Soil risk
    if (riskData.soil_risk && riskData.soil_risk.risk_score !== undefined) {
      riskFactors.push({
        title: "Soil Risk",
        score: riskData.soil_risk.risk_score,
        description: "Soil quality and erosion risk"
      });
    }
    
    // Market risk
    if (riskData.market_risk && riskData.market_risk.risk_score !== undefined) {
      riskFactors.push({
        title: "Market Risk",
        score: riskData.market_risk.risk_score,
        description: "Market conditions and crop prices"
      });
    }
    
    // Historical risk (if present)
    if (riskData.historical_risk && riskData.historical_risk.risk_score !== undefined) {
      riskFactors.push({
        title: "Historical Risk",
        score: riskData.historical_risk.risk_score,
        description: "Historical farm performance"
      });
    }
    
    return riskFactors;
  };
  
  const riskFactors = getRiskData();

  return (
    <Card className={classes.card}>
      <CardHeader
        title="Risk Assessment"
        className={classes.cardHeader}
      />
      <CardContent>
        {!hasRiskData ? (
          <Typography variant="body1" align="center" style={{ marginBottom: 16 }}>
            No risk assessment data available for this farm.
          </Typography>
        ) : riskFactors.length === 0 ? (
          <Typography variant="body1" align="center" style={{ marginBottom: 16 }}>
            Risk assessment data format is incorrect or incomplete.
          </Typography>
        ) : (
          <Grid container spacing={2}>
            {riskFactors.map((risk, index) => {
              const riskLevel = getRiskLevel(risk.score);
              return (
                <Grid item xs={12} key={index}>
                  <div className={classes.riskItem}>
                    <div className={classes.riskScore}>
                      <div className={classes.riskIcon}>
                        {riskLevel.icon}
                      </div>
                      <Typography variant="subtitle1">
                        {risk.title}
                      </Typography>
                    </div>
                    <LinearProgress
                      variant="determinate"
                      value={risk.score}
                      className={classes.progress}
                      style={{ backgroundColor: `${riskLevel.color}40`, color: riskLevel.color }}
                    />
                    <div className={classes.progressLabel}>
                      <Typography variant="body2" color="textSecondary">
                        {risk.description}
                      </Typography>
                      <Typography variant="body2" style={{ color: riskLevel.color }}>
                        {riskLevel.label} ({risk.score}%)
                      </Typography>
                    </div>
                  </div>
                </Grid>
              );
            })}
          </Grid>
        )}
        {hasRiskData && (
          <div className={classes.recommendationBox}>
            <Typography variant="subtitle1">
              <Info className={classes.recommendationIcon} color="primary" />
              Key Recommendations
            </Typography>
            {data.riskAssessment.key_recommendations && data.riskAssessment.key_recommendations.length > 0 ? (
              <div style={{ marginTop: 8 }}>
                {data.riskAssessment.key_recommendations.map((recommendation, index) => (
                  <Typography key={index} variant="body2" style={{ marginTop: 4 }}>
                    • {recommendation}
                  </Typography>
                ))}
              </div>
            ) : (
              <Typography variant="body2" style={{ marginTop: 8 }}>
                No recommendations available for this farm.
              </Typography>
            )}
            {data.riskAssessment.last_updated && (
              <Typography variant="caption" color="textSecondary" style={{ display: 'block', marginTop: 16 }}>
                Last updated: {new Date(data.riskAssessment.last_updated).toLocaleString()}
              </Typography>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
